/**
 * Admin Projects Page
 *
 * Lists all projects with search, filtering, pagination, and CRUD actions.
 */

import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Card,
  CardContent,
  DataTable,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Inline,
  Input,
  PageHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Stack,
  Text,
} from '@cortex/ui';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  ExternalLink,
  FolderKanban,
  Pencil,
  Plus,
  Search,
  Trash2,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import { CreateProjectWizard } from '@/components/admin/CreateProjectWizard';
import { useApi, useFetchWithAuth } from '@/hooks/use-api';
import {
  DEFAULT_PAGINATION,
  formatDate,
  type PaginationInfo,
} from '@/lib/admin-utils';

export const Route = createFileRoute('/_app/admin/projects/')({
  component: AdminProjectsPage,
});

interface Organization {
  id: string;
  name: string;
  slug: string;
}

interface ProjectRecord {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  repo_url: string | null;
  organization_id: string;
  organizations: Organization | null;
  requestCount: number;
  created_at: string;
  updated_at: string;
}

function AdminProjectsPage() {
  const navigate = useNavigate();
  const getApi = useApi();
  const fetchWithAuth = useFetchWithAuth();
  const [projects, setProjects] = useState<ProjectRecord[]>([]);
  const [pagination, setPagination] =
    useState<PaginationInfo>(DEFAULT_PAGINATION);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [searchDebounce, setSearchDebounce] = useState('');
  const [orgFilter, setOrgFilter] = useState<string>('all');

  // Organizations for dropdown
  const [organizations, setOrganizations] = useState<Organization[]>([]);

  // Create wizard state
  const [createWizardOpen, setCreateWizardOpen] = useState(false);

  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingProject, setDeletingProject] = useState<ProjectRecord | null>(
    null
  );
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounce(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch organizations for dropdown
  const fetchOrganizations = useCallback(async () => {
    try {
      const api = await getApi();
      const response = await api.admin.organizations.$get({
        query: { limit: '100' },
      });
      if (response.ok) {
        const json = await response.json();
        if ('data' in json) {
          const data = json.data as unknown as { items: Organization[] };
          setOrganizations(data.items);
        }
      }
    } catch {
      // Silently fail - organizations dropdown will be empty
    }
  }, [getApi]);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  // Fetch projects
  const fetchProjects = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const query: Record<string, string> = {
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
      };
      if (searchDebounce) {
        query.search = searchDebounce;
      }
      if (orgFilter !== 'all') {
        query.organizationId = orgFilter;
      }

      const api = await getApi();
      const response = await api.adminProjects.index.$get({ query });
      if (!response.ok) {
        throw new Error('Failed to fetch projects');
      }

      const json = await response.json();
      if ('data' in json) {
        const data = json.data as unknown as {
          items: ProjectRecord[];
          pagination: PaginationInfo;
        };
        setProjects(data.items);
        setPagination(data.pagination);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, searchDebounce, orgFilter, getApi]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // Open delete modal
  const openDeleteModal = (project: ProjectRecord) => {
    setDeletingProject(project);
    setDeleteError(null);
    setDeleteModalOpen(true);
  };

  // Delete project
  const handleDelete = async () => {
    if (!deletingProject) return;

    setDeleting(true);
    setDeleteError(null);

    try {
      const response = await fetchWithAuth(
        `/admin/projects/${deletingProject.id}`,
        {
          method: 'DELETE',
        }
      );

      if (!response.ok && response.status !== 204) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to delete project');
      }

      setDeleteModalOpen(false);
      setDeletingProject(null);
      fetchProjects();
    } catch (err) {
      setDeleteError(
        err instanceof Error ? err.message : 'Failed to delete project'
      );
    } finally {
      setDeleting(false);
    }
  };

  // Table columns
  const columns = [
    {
      key: 'name',
      header: 'Project',
      sortable: true,
      render: (project: ProjectRecord) => (
        <Inline gap="sm">
          <Stack className="bg-primary/10 rounded-lg p-2">
            <FolderKanban className="text-primary h-4 w-4" />
          </Stack>
          <Stack gap="none">
            <Text weight="medium">{project.name}</Text>
            <Text color="muted" size="xs">
              {project.slug}
            </Text>
          </Stack>
        </Inline>
      ),
    },
    {
      key: 'organization',
      header: 'Organization',
      render: (project: ProjectRecord) => (
        <Text size="sm" color={project.organizations ? 'default' : 'muted'}>
          {project.organizations?.name || 'No organization'}
        </Text>
      ),
    },
    {
      key: 'requests',
      header: 'Requests',
      render: (project: ProjectRecord) => (
        <Badge variant={project.requestCount > 0 ? 'default' : 'outline'}>
          {project.requestCount}
        </Badge>
      ),
    },
    {
      key: 'repository',
      header: 'Repository',
      render: (project: ProjectRecord) =>
        project.repo_url ? (
          <Button
            variant="link"
            asChild
            className="text-primary inline-flex items-center gap-1 text-base hover:underline"
          >
            <a
              href={project.repo_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              View
              <ExternalLink className="h-3 w-3" />
            </a>
          </Button>
        ) : (
          <Text size="sm" color="muted">
            Not linked
          </Text>
        ),
    },
    {
      key: 'created_at',
      header: 'Created',
      sortable: true,
      render: (project: ProjectRecord) => (
        <Text size="sm" color="muted">
          {formatDate(project.created_at)}
        </Text>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (project: ProjectRecord) => (
        <Inline gap="xs" justify="end">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              navigate({
                to: '/admin/projects/$id',
                params: { id: project.id },
              });
            }}
            className="h-8 w-8 p-0"
          >
            <Pencil className="h-4 w-4" />
            <Text as="span" className="sr-only">
              Edit
            </Text>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              openDeleteModal(project);
            }}
            className="text-destructive hover:text-destructive h-8 w-8 p-0"
          >
            <Trash2 className="h-4 w-4" />
            <Text as="span" className="sr-only">
              Delete
            </Text>
          </Button>
        </Inline>
      ),
    },
  ];

  return (
    <Stack gap="lg" className="p-6">
      {/* Header */}
      <PageHeader
        title="Projects"
        subtitle="Manage projects across all organizations"
        actions={
          <Button onClick={() => setCreateWizardOpen(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            New Project
          </Button>
        }
      />

      {/* Filters */}
      <Inline gap="md" wrap>
        <Stack className="min-w-[200px] flex-1">
          <Input
            type="text"
            placeholder="Search projects..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            leftAddon={<Search className="h-4 w-4" />}
          />
        </Stack>
        <Select value={orgFilter} onValueChange={setOrgFilter}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Filter by organization" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All organizations</SelectItem>
            {organizations.map((org) => (
              <SelectItem key={org.id} value={org.id}>
                {org.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Inline>

      {/* Error */}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>
            {error}
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchProjects}
              className="mt-2"
            >
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Table */}
      <DataTable
        columns={columns}
        data={projects}
        loading={loading}
        emptyMessage="No projects found"
        pagination={{
          page: pagination.page,
          pageSize: pagination.limit,
          total: pagination.total,
        }}
        onPageChange={(page) => setPagination((prev) => ({ ...prev, page }))}
        onRowClick={(project) => {
          navigate({ to: '/admin/projects/$id', params: { id: project.id } });
        }}
      />

      {/* Create Project Wizard */}
      <CreateProjectWizard
        open={createWizardOpen}
        onClose={() => setCreateWizardOpen(false)}
        organizations={organizations}
        onSuccess={() => {
          fetchProjects();
        }}
      />

      {/* Delete Confirmation Modal */}
      <Dialog
        open={deleteModalOpen}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteModalOpen(false);
            setDeletingProject(null);
            setDeleteError(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Project</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this project? This action cannot
              be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            <Stack gap="md">
              {deleteError && (
                <Alert variant="destructive">
                  <AlertDescription>{deleteError}</AlertDescription>
                </Alert>
              )}
              {deletingProject && (
                <Card className="bg-surface-2">
                  <CardContent className="p-4">
                    <Inline gap="sm">
                      <Stack className="bg-primary/10 rounded-lg p-2">
                        <FolderKanban className="text-primary h-5 w-5" />
                      </Stack>
                      <Stack gap="none">
                        <Text weight="medium">{deletingProject.name}</Text>
                        <Text color="muted" size="sm">
                          {deletingProject.organizations?.name ||
                            'No organization'}
                        </Text>
                      </Stack>
                    </Inline>
                  </CardContent>
                </Card>
              )}
              <Text color="muted" size="sm">
                All requests and data associated with this project will also be
                deleted.
              </Text>
            </Stack>
          </DialogBody>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDeleteModalOpen(false);
                setDeletingProject(null);
                setDeleteError(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              loading={deleting}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Stack>
  );
}
